import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

// TODO: Update Sentry configuration
if (process.env.REACT_APP_ENV !== 'development') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [
      Sentry.httpClientIntegration({ failedRequestStatusCodes: [[500, 505], 507] }),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ['proxy-cheap-integration-sentry-key'],
        behaviour: 'drop-error-if-contains-third-party-frames',
      }),
    ],
    environment: process.env.REACT_APP_ENV,
    debug: process.env.REACT_APP_ENV !== 'production',
    release: process.env.REACT_APP_SENTRY_RELEASE,

    // This options is required for capturing headers and cookies
    sendDefaultPii: true,
    beforeSend(event, hint) {
      const error = hint.originalException as string | { message?: string };
      if (error === 'Timeout') return null;

      const errorMessage = typeof error === 'string' ? error : error?.message;

      if (
        errorMessage?.match(/Failed to fetch/g) ||
        // ignore Stripe.js failing to load (usually caused by ad-blockers/privacy extensions) and
        errorMessage?.match(/Failed to load Stripe\.js/g) ||
        // PostHog session recording failing to clone a mutation record from a cross-origin Stripe
        // Elements iframe when posting it to its compression worker
        errorMessage?.match(/The object can not be cloned/g) ||
        // PostHog's own request layer aborting itself via its internal request timeout
        errorMessage?.match(/signal is aborted without reason/g) ||
        // browser extensions (password managers, ad blockers, etc.) mid-flight messaging
        // their background worker when we close the tab on /payments/callback
        errorMessage?.match(/Invalid call to runtime\.sendMessage/g) ||
        // browser extension's content script messaging a background/popup page that has
        // already been torn down (tab closed/navigated away) - not our code, no stack trace
        errorMessage?.match(/Could not establish connection\. Receiving end does not exist\./g) ||
        // PostHog callback firing before its session recording plugin has initialized
        errorMessage?.match(/Called on script loaded before session recording is available/g) ||
        // Browser's Service Worker system transiently shutting down (e.g. mid-navigation/tab
        // close) while our push-notifications code queries the registration - already guarded
        // with try/catch at every known call site, this is the browser surfacing it anyway
        errorMessage?.match(/Failed to get a ServiceWorkerRegistration: The Service Worker system has shutdown/g)
      ) {
        return null;
      }

      // ignore BE validation errors
      if (
        event.breadcrumbs?.[1]?.category === 'xhr' &&
        (event.breadcrumbs[1]?.data?.status_code === 403 || !event.breadcrumbs[1]?.data?.status_code) &&
        (event.breadcrumbs[0]?.message?.match(/Non-Error promise rejection captured with keys/g) ||
          event.breadcrumbs[0]?.message?.match(/Object captured as promise rejection with keys/g))
      )
        return null;

      // any 3rd party request as current app requests category is xhr
      if (event.breadcrumbs?.[0]?.category === 'fetch' || event.breadcrumbs?.[1]?.category === 'fetch') return null;

      // ignore recent errors came after 401 response
      if (
        (event.breadcrumbs?.[0]?.category === 'xhr' && event.breadcrumbs[0]?.data?.status_code === 401) ||
        (event.breadcrumbs?.[1]?.category === 'xhr' && event.breadcrumbs[1]?.data?.status_code === 401)
      )
        return null;

      // Everything past this point is a genuine app crash or failed request (see
      // httpClientIntegration above) - start a PostHog session recording so we get replay context
      // for it, without paying for recording every ordinary session (see index.tsx sampleRate).
      // This only captures what happens from this point forward, not the lead-up to the error.
      try {
        posthog.startSessionRecording(true);
      } catch {
        // noop - PostHog may not be loaded yet (e.g. very early boot errors)
      }

      return event;
    },
    ignoreErrors: [],
    denyUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      /gstatic.com\/recaptcha/i,
      /gstatic.com\/recaptcha\/releases\/[a-zA-Z0-9]+\/recaptcha__[a-z]+_[a-z]+.js/,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      // Google Translate
      /translate\.googleapis\.com/i,
      // Cloudflare
      /static\.cloudflareinsights\.com/i,
      // PostHog
      /posthog/,
      // Hotjar injected
      /script\.hotjar\.com/i,
    ],
  });
}
